import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { useCallback, useMemo } from "react";
import { useProductDetailInitial } from "./productDetail.context";
import {
  getProductDetail as getProductDetailThunk,
  getBottomGroupProducts as getBottomGroupProductsThunk,
  getModalContent as getModalContentThunk,
} from "./productDetail.thunk";
import {
  resetProductDetail,
  resetModalContent,
  setSelectedColorIndex,
  setSelectedSizeIndex,
  setSelectedIndices,
  setLoadingColorIndex,
  setIsColorChanging,
  setPendingColorSlug,
  setCurrentSlug,
  setVariantFavorite,
} from "./productDetail.slice";
import { isLoading, isSuccess, isError } from "@/app/utils/asyncStatus";
import { ModalType } from "./productDetail.types";

/**
 * useProductDetail tüm `state.productDetail` slice'ına abone. Ürün kartı bu
 * slice'tan hiçbir şey okumuyor, sadece modal içeriğini çekiyor — ama abonelik
 * yüzünden thunk pending'e geçtiği anda 84 kart yeniden render oluyordu.
 * Bu sürüm yalnızca dispatch eder.
 */
export const useFetchModalContent = () => {
  const dispatch = useAppDispatch();
  return useCallback(
    async (productId: number, type: ModalType) => {
      const result = await dispatch(getModalContentThunk({ productId, type }));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );
};

const useProductDetail = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((store) => store.productDetail);
  const initialData = useProductDetailInitial();

  // SSR'da Redux boş başlar — context'teki initialData fallback olarak kullanılır.
  // useEffect dispatch edince Redux devralır, component'lar yeniden render olmaz (aynı veri).
  const product = state.data?.product ?? initialData?.product ?? null;
  const breadcrumbs = state.data?.breadcrumbs ?? initialData?.breadcrumbs ?? [];
  const bottomGroupSections = state.data?.bottom_group_products ?? initialData?.bottom_group_products ?? [];

  const statusFlags = useMemo(
    () => ({
      isLoading: isLoading(state.status),
      isSuccess: isSuccess(state.status),
      isError: isError(state.status),
    }),
    [state.status],
  );

  const modalStatusFlags = useMemo(
    () => ({
      isModalLoading: isLoading(state.modalStatus),
      isModalSuccess: isSuccess(state.modalStatus),
      isModalError: isError(state.modalStatus),
    }),
    [state.modalStatus],
  );

  const fetchProductDetail = useCallback(
    async (slug: string, variantId?: number, silent?: boolean) => {
      const result = await dispatch(getProductDetailThunk({ slug, variantId, silent }));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchBottomGroupProducts = useCallback(
    async (productId: number, key: string) => {
      const result = await dispatch(
        getBottomGroupProductsThunk({ productId, key }),
      );
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const getBottomGroupProductsByKey = useCallback(
    (key: string) => state.bottomGroupProducts[key] ?? [],
    [state.bottomGroupProducts],
  );

  const getBottomGroupStatusByKey = useCallback(
    (key: string) => {
      const keyStatus = state.bottomGroupStatus[key] ?? "idle";
      return {
        status: keyStatus,
        isLoading: isLoading(keyStatus),
        isSuccess: isSuccess(keyStatus),
        isError: isError(keyStatus),
      };
    },
    [state.bottomGroupStatus],
  );

  const fetchModalContent = useCallback(
    async (productId: number, type: ModalType) => {
      const result = await dispatch(getModalContentThunk({ productId, type }));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchModalContentIfNeeded = useCallback(
    async (productId: number, type: ModalType): Promise<boolean> => {
      if (
        state.modalContent &&
        state.modalType === type &&
        !isLoading(state.modalStatus)
      ) {
        return true;
      }
      if (isLoading(state.modalStatus)) {
        return false;
      }
      const result = await dispatch(getModalContentThunk({ productId, type }));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch, state.modalContent, state.modalType, state.modalStatus],
  );

  const reset = useCallback(() => {
    dispatch(resetProductDetail());
  }, [dispatch]);

  const resetModal = useCallback(() => {
    dispatch(resetModalContent());
  }, [dispatch]);

  const updateSelectedColorIndex = useCallback(
    (index: number) => {
      dispatch(setSelectedColorIndex(index));
    },
    [dispatch],
  );

  const updateSelectedSizeIndex = useCallback(
    (index: number) => {
      dispatch(setSelectedSizeIndex(index));
    },
    [dispatch],
  );

  const updateSelectedIndices = useCallback(
    (colorIndex: number, sizeIndex: number) => {
      dispatch(setSelectedIndices({ colorIndex, sizeIndex }));
    },
    [dispatch],
  );

  const updateLoadingColorIndex = useCallback(
    (index: number | null) => {
      dispatch(setLoadingColorIndex(index));
    },
    [dispatch],
  );

  const updateIsColorChanging = useCallback(
    (value: boolean) => {
      dispatch(setIsColorChanging(value));
    },
    [dispatch],
  );

  const updatePendingColorSlug = useCallback(
    (slug: string | null) => {
      dispatch(setPendingColorSlug(slug));
    },
    [dispatch],
  );

  const updateCurrentSlug = useCallback(
    (slug: string) => {
      dispatch(setCurrentSlug(slug));
    },
    [dispatch],
  );

  /** Toggle sonrası renk varyantının favori durumunu güncelle. */
  const updateVariantFavorite = useCallback(
    (variantId: number, isFavorited: boolean) => {
      dispatch(setVariantFavorite({ variantId, isFavorited }));
    },
    [dispatch],
  );

  return {
    product,
    breadcrumbs,
    bottomGroupSections,
    error: state.error,
    modalContent: state.modalContent,
    modalType: state.modalType,
    selectedColorIndex: state.selectedColorIndex,
    selectedSizeIndex: state.selectedSizeIndex,
    loadingColorIndex: state.loadingColorIndex,
    isColorChanging: state.isColorChanging,
    pendingColorSlug: state.pendingColorSlug,
    currentSlug: state.currentSlug,
    isAuthDataSettled: state.isAuthDataSettled,
    status: state.status,
    modalStatus: state.modalStatus,
    dispatch,
    ...statusFlags,
    ...modalStatusFlags,
    fetchProductDetail,
    fetchBottomGroupProducts,
    getBottomGroupProductsByKey,
    getBottomGroupStatusByKey,
    fetchModalContent,
    fetchModalContentIfNeeded,
    reset,
    resetModal,
    updateSelectedColorIndex,
    updateSelectedSizeIndex,
    updateSelectedIndices,
    updateLoadingColorIndex,
    updateIsColorChanging,
    updatePendingColorSlug,
    updateCurrentSlug,
    updateVariantFavorite,
  };
};

export default useProductDetail;
